/**
 * Agency workspaces (brand switcher).
 *
 *   GET  /api/v1/users/me/workspaces
 *   POST /api/v1/tenants/me/workspaces/{id}/switch
 *
 * Workspaces are flag-OFF today, so every call here degrades to "no workspaces"
 * (the header switcher stays hidden) on 403/404/501/503/network errors.
 */

import { api, getActiveWorkspaceId, setActiveWorkspaceId } from '@/lib/api/client';

export interface Workspace {
  id: string;
  name: string;
  slug?: string;
  /** Brand identity: `color` (hex) and `logo_url` are the keys the UI writes. */
  branding?: WorkspaceBranding | null;
  created_at?: string;
  /** True when the backend marks it as the caller's current workspace. */
  current?: boolean;
}

export interface WorkspaceBranding {
  color?: string;
  logo_url?: string;
  [key: string]: unknown;
}

function normalize(raw: unknown): Workspace[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (((raw as Record<string, unknown>).workspaces ??
          (raw as Record<string, unknown>).items ??
          (raw as Record<string, unknown>).data ??
          []) as unknown[])
      : [];
  const out: Workspace[] = [];
  for (const w of Array.isArray(list) ? list : []) {
    if (!w || typeof w !== 'object') continue;
    const o = w as Record<string, unknown>;
    const id = o.id ?? o.workspace_id;
    if (typeof id !== 'string' && typeof id !== 'number') continue;
    const name = o.name ?? o.display_name ?? o.slug ?? String(id);
    out.push({
      id: String(id),
      name: String(name),
      slug: typeof o.slug === 'string' ? o.slug : undefined,
      branding:
        o.branding && typeof o.branding === 'object' ? (o.branding as WorkspaceBranding) : null,
      created_at: typeof o.created_at === 'string' ? o.created_at : undefined,
      current: Boolean(o.current ?? o.is_current ?? o.active ?? o.is_active),
    });
  }
  return out;
}

/** Workspaces the caller can act in; `[]` when the feature is off / unreachable. */
export async function listMyWorkspaces(): Promise<Workspace[]> {
  try {
    return normalize(await api.get<unknown>('/api/v1/users/me/workspaces'));
  } catch {
    return [];
  }
}

/** Like `listMyWorkspaces` but `null` on failure, so callers can tell "none" from "unreachable". */
export async function listMyWorkspacesOrNull(): Promise<Workspace[] | null> {
  try {
    return normalize(await api.get<unknown>('/api/v1/users/me/workspaces'));
  } catch {
    return null;
  }
}

/** Switch the active workspace and pin it as `X-Workspace-Id` on every request. */
export async function switchWorkspace(id: string): Promise<void> {
  await api.post(`/api/v1/tenants/me/workspaces/${encodeURIComponent(id)}/switch`);
  setActiveWorkspaceId(id);
}

/** "Tout le compte": drop `X-Workspace-Id` so calls scope to the whole tenant. */
export function clearWorkspace(): void {
  setActiveWorkspaceId(null);
}

// ---------------------------------------------------------------------------
// Admin CRUD (tenant-service /tenants/me/workspaces; owner/admin for writes)
// ---------------------------------------------------------------------------

const BASE = '/api/v1/tenants/me/workspaces';

export interface WorkspaceInput {
  name: string;
  branding?: WorkspaceBranding | null;
}

/** Unlike `listMyWorkspaces`, errors propagate so the admin page can show them. */
export async function listWorkspaces(): Promise<Workspace[]> {
  return normalize(await api.get<unknown>(BASE));
}

export async function createWorkspace(input: WorkspaceInput): Promise<Workspace> {
  const [w] = normalize([await api.post<unknown>(BASE, input)]);
  return w;
}

export async function updateWorkspace(id: string, patch: Partial<WorkspaceInput>): Promise<Workspace> {
  const [w] = normalize([await api.patch<unknown>(`${BASE}/${encodeURIComponent(id)}`, patch)]);
  return w;
}

export function deleteWorkspace(id: string): Promise<void> {
  return api.delete<void>(`${BASE}/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Workspace-scoped role assignment (rbac-service via the /api/v1/roles prefix)
// ---------------------------------------------------------------------------

export interface WorkspaceAssignment {
  id: string;
  user_id: string;
  role_id: string;
  tenant_id: string;
  workspace_id: string;
}

/** Workspace-scoped holders of a role; `workspaceId` omitted = every workspace. */
export function listWorkspaceMembers(roleId: string, workspaceId?: string): Promise<WorkspaceAssignment[]> {
  const q = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
  return api.get<WorkspaceAssignment[]>(
    `/api/v1/roles/${encodeURIComponent(roleId)}/workspace-members${q}`,
  );
}

export function assignWorkspaceRole(roleId: string, userId: string, workspaceId: string): Promise<unknown> {
  return api.post(
    `/api/v1/roles/${encodeURIComponent(roleId)}/members/${encodeURIComponent(userId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
  );
}

export function removeWorkspaceRole(roleId: string, userId: string, workspaceId: string): Promise<void> {
  return api.delete<void>(
    `/api/v1/roles/${encodeURIComponent(roleId)}/members/${encodeURIComponent(userId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
  );
}

export { getActiveWorkspaceId };
